import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, SerifHeading } from "@/components/site/primitives";
import { SITE, HIGHLIGHTS, SERVICES, WHY_CHOOSE, FAQS } from "@/lib/site-data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <SectionLabel testId="hero-eyebrow">
                Advocate · Bengaluru
              </SectionLabel>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-headline"
              className="display mt-8 text-[2.75rem] sm:text-6xl lg:text-[5rem] leading-[1.03]"
            >
              Protecting Your
              <br />
              Property.{" "}
              <span className="italic text-[#B8912C]">Securing</span>
              <br />
              Your Interests.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              data-testid="hero-subheading"
              className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed"
            >
              Over 6 years of experience advising businesses, developers, startups,
              and individuals on real estate transactions, legal documentation,
              contract drafting, due diligence, and regulatory compliance.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-book"
                className="group inline-flex items-center justify-center gap-3 bg-navy text-white px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-[#132644] transition-all"
              >
                Book a Consultation
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center justify-center gap-3 border border-navy/80 text-navy px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-navy hover:text-white transition-all"
              >
                View Services
              </Link>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-md" data-testid="hero-metrics">
              {[
                { n: "6+", l: "Years Practice" },
                { n: "50+", l: "Contract Types" },
                { n: "4", l: "Sectors Served" },
              ].map((m) => (
                <div key={m.l}>
                  <div className="font-serif text-4xl text-navy">{m.n}</div>
                  <div className="text-[11px] mt-1 uppercase tracking-[0.2em] text-muted-foreground">
                    {m.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -top-6 -left-6 w-32 h-32 border border-gold hidden md:block" aria-hidden />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-navy/5 hidden md:block" aria-hidden />
              <div className="relative overflow-hidden">
                <img
                  src={SITE.images.portrait}
                  alt="Advocate Samikshaa K portrait"
                  data-testid="hero-portrait"
                  className="w-full h-[480px] md:h-[620px] object-cover object-top"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                  <div className="eyebrow text-gold">In Practice</div>
                  <div className="font-serif text-2xl mt-1">Advocate Samikshaa K</div>
                  <div className="text-xs text-white/80 mt-1">
                    Real Estate · Contracts · Advisory
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="border-y border-border bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border">
            {HIGHLIGHTS.map((h, idx) => (
              <Reveal key={h.label} delay={idx * 0.05}>
                <div
                  data-testid={`highlight-${idx}`}
                  className="bg-white p-6 md:p-8 h-full flex flex-col justify-between min-h-[160px]"
                >
                  <div className="eyebrow text-[#B8912C]">{h.meta}</div>
                  <div className="font-serif text-xl md:text-2xl text-navy leading-tight mt-6">
                    {h.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>About</SectionLabel>
              <SerifHeading className="mt-6">
                A practice built on precision and practicality.
              </SerifHeading>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Advocate Samikshaa K is a Bengaluru-based lawyer specializing in
                Real Estate Law, Contract Drafting, Due Diligence, Legal Advisory,
                and Documentation.
              </p>
              <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
                She has worked across the real estate, technology, nonprofit, and
                corporate sectors, advising organizations on complex legal matters
                while ensuring practical and commercially sound solutions.
              </p>
              <Link
                to="/about"
                data-testid="about-preview-cta"
                className="mt-10 inline-flex items-center gap-3 text-navy text-xs uppercase tracking-[0.24em] font-semibold link-underline"
              >
                Read Full Biography
                <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32 bg-white border-y border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <Reveal>
                <SectionLabel>Practice Areas</SectionLabel>
                <SerifHeading className="mt-6 max-w-2xl">
                  Legal services, refined for the way real business works.
                </SerifHeading>
              </Reveal>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="text-xs uppercase tracking-[0.24em] font-semibold link-underline text-navy shrink-0"
            >
              View all services →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border mt-16">
            {SERVICES.map((s, idx) => (
              <Reveal key={s.id} delay={idx * 0.06}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group block bg-white p-8 md:p-10 h-full hover:bg-[#FAFAFA] transition-colors"
                >
                  <div className="eyebrow text-[#B8912C]">
                    0{idx + 1} / {SERVICES.length}
                  </div>
                  <h3 className="font-serif text-2xl md:text-[28px] text-navy mt-6 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                    {s.summary}
                  </p>
                  <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-navy">
                    Learn more
                    <ArrowRight
                      size={14}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionLabel>Why Choose Samikshaa</SectionLabel>
              <SerifHeading className="mt-6">
                Considered counsel. Commercial clarity.
              </SerifHeading>
              <p className="mt-6 text-muted-foreground max-w-md">
                Every engagement is grounded in careful documentation, transparent
                communication, and a working understanding of your commercial context.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border border border-border">
              {WHY_CHOOSE.map((w, idx) => (
                <Reveal key={w} delay={idx * 0.05}>
                  <li
                    data-testid={`why-item-${idx}`}
                    className="bg-white p-6 md:p-8 flex items-start gap-4 h-full"
                  >
                    <span className="font-serif text-gold text-lg">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-lg md:text-xl text-navy leading-snug">
                      {w}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-white border-t border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionLabel>FAQ</SectionLabel>
              <SerifHeading className="mt-6">
                Answers before you ask.
              </SerifHeading>
              <p className="mt-6 text-muted-foreground">
                A quick reference to how the practice typically engages with clients.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, idx) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${idx}`}
                  data-testid={`faq-item-${idx}`}
                  className="border-b border-border"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${idx}`}
                    className="text-left font-serif text-lg md:text-xl text-navy hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-content-${idx}`}
                    className="text-muted-foreground leading-relaxed pb-6 pr-8"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 md:py-28 bg-navy text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <div>
            <div className="eyebrow text-gold">Ready to Consult</div>
            <h3 className="font-serif text-3xl md:text-5xl mt-4 max-w-2xl leading-tight">
              Have a property, contract, or advisory matter? Let&apos;s begin with a
              conversation.
            </h3>
          </div>
          <Link
            to="/contact"
            data-testid="cta-strip-button"
            className="inline-flex items-center gap-3 bg-white text-navy px-7 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:bg-gold hover:text-navy transition-colors"
          >
            Book Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
